import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Fab, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from './contexts/AuthContext';
import routesConfig from './config/routesConfig';
import Roles from './config/roles';
import AgentDrawer from './components/AgentDrawer';

// Customer-facing / public surfaces where the AI assistant must never appear,
// even if a CRM admin session exists in the same browser.
const AGENT_HIDDEN_PREFIXES = [
    '/portal',
    '/sign',            // covers /sign/:token and /sign-contract/:token
    '/login',
    '/register',
    '/setup-account',
    '/subscription/success',
];

// AI assistant FAB + drawer. ADMIN-only, and hidden on portal/public routes.
// Lives inside <Router> so it can react to the current location.
const AgentLauncher = () => {
    const { authState, role } = useContext(AuthContext);
    const location = useLocation();

    const [agentOpen, setAgentOpen]                 = useState(false);
    const [agentLoading, setAgentLoading]           = useState(false);
    const [hasUnreadResponse, setHasUnreadResponse] = useState(false);
    const [agentDefaultTab, setAgentDefaultTab]     = useState('chat');

    // Listen for custom event to open the drawer on a specific tab
    useEffect(() => {
        const handler = (e) => {
            const tab = e?.detail?.tab || 'chat';
            setAgentDefaultTab(tab);
            setAgentOpen(true);
            setHasUnreadResponse(false);
        };
        window.addEventListener('open-agent-drawer', handler);
        return () => window.removeEventListener('open-agent-drawer', handler);
    }, []);

    const hiddenHere = AGENT_HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p));
    if (!authState.isLoggedIn || role !== Roles.ADMIN || hiddenHere) return null;

    const handleToggleAgent = () => {
        const next = !agentOpen;
        setAgentOpen(next);
        if (next) {
            setHasUnreadResponse(false); // clear badge when opening
            setAgentDefaultTab('chat');  // reset to default tab when toggling via FAB
        }
    };

    const handleResponseReceived = () => {
        if (!agentOpen) setHasUnreadResponse(true);
    };

    // Derived FAB state
    const fabState = agentOpen ? 'open'
                   : agentLoading ? 'thinking'
                   : hasUnreadResponse ? 'ready'
                   : 'idle';

    const fabTooltip = fabState === 'open'    ? 'Close AI assistant'
                     : fabState === 'thinking' ? 'AI is thinking…'
                     : fabState === 'ready'    ? 'Response ready — open AI'
                     : 'AI Assistant';

    return (
        <>
            <Tooltip title={fabTooltip} placement="left">
                {/* Wrapper Box needed so Tooltip works around a non-button child */}
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 28,
                        right: 28,
                        zIndex: 1300,
                        display: 'inline-flex',
                    }}
                >
                    {/* Spinning ring — thinking state */}
                    {fabState === 'thinking' && (
                        <CircularProgress
                            size={56}
                            thickness={2}
                            sx={{
                                color: '#7dd3fc',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 1,
                                pointerEvents: 'none',
                            }}
                        />
                    )}

                    {/* Pulsing glow ring — ready state */}
                    {fabState === 'ready' && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                pointerEvents: 'none',
                                '@keyframes readyPulse': {
                                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(125,211,252,0.55)' },
                                    '55%': { boxShadow: '0 0 0 9px rgba(125,211,252,0)' },
                                },
                                animation: 'readyPulse 1.8s ease-out infinite',
                            }}
                        />
                    )}

                    <Fab
                        size="medium"
                        onClick={handleToggleAgent}
                        sx={{
                            background: 'linear-gradient(135deg, #4e90b5 0%, #427898 50%, #2d5f80 100%)',
                            color: '#fff',
                            boxShadow: fabState === 'ready'
                                ? '0 4px 22px rgba(125,211,252,0.55)'
                                : '0 4px 20px rgba(66,120,152,0.40)',
                            transition: 'box-shadow 0.3s, transform 0.2s',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #4e90b5 0%, #427898 50%, #2d5f80 100%)',
                                filter: 'brightness(1.1)',
                                transform: 'scale(1.05)',
                            },
                            '&:active': { transform: 'scale(0.97)' },
                        }}
                    >
                        {agentOpen ? <CloseIcon /> : <AutoAwesomeIcon />}
                    </Fab>

                    {/* Unread dot — ready state */}
                    {fabState === 'ready' && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 3,
                                right: 3,
                                width: 11,
                                height: 11,
                                borderRadius: '50%',
                                bgcolor: '#10B981',
                                border: '2px solid #fff',
                                pointerEvents: 'none',
                            }}
                        />
                    )}
                </Box>
            </Tooltip>

            <AgentDrawer
                open={agentOpen}
                onClose={() => setAgentOpen(false)}
                onLoadingChange={setAgentLoading}
                onResponseReceived={handleResponseReceived}
                defaultTab={agentDefaultTab}
            />
        </>
    );
};

const App = () => {
    const { authState, role, refreshToken, hasActiveSubscription } = useContext(AuthContext);

    useEffect(() => {
        if (!authState.isLoggedIn) return;
        const interval = setInterval(refreshToken, 15000);
        return () => clearInterval(interval);
    }, [authState.isLoggedIn]);

    const renderRoute = ({ path, element, public: isPublic, roles }) => {
        if (!authState.isLoggedIn && !isPublic)
            return <Route key={path} path={path} element={<Navigate to="/login" replace />} />;
        if (authState.isLoggedIn && roles && !roles.includes(role))
            return <Route key={path} path={path} element={<Navigate to={role === Roles.USER ? "/time-tracking" : "/home"} replace />} />;
        return <Route key={path} path={path} element={element} />;
    };

    return (
        <Router>
            <Routes>
                <Route
                    index
                    element={
                        authState.isLoggedIn
                            ? !hasActiveSubscription
                                ? <Navigate to="/pricing" replace />
                                : <Navigate to={role === Roles.USER ? "/time-tracking" : "/home"} replace />
                            : <Navigate to="/login" replace />
                    }
                />
                {routesConfig.map(renderRoute)}
            </Routes>

            <AgentLauncher />
        </Router>
    );
};

export default App;
